import { NgTemplateOutlet } from '@angular/common';

import {
  ChangeDetectionStrategy,
  Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges,
  TemplateRef, ViewChild, ViewEncapsulation
} from '@angular/core';

import { SdSubcategoryComponent } from './subcategory/subcategory.component';
import type { ISubcategory } from './subcategory/subcategory.interface';

import {
  BrandThemesEnum, ILink, ImageRatioEnum, PositionEnum, PushSizeEnum, SdLibConfig, SdListComponent,
  SdPushCardComponent, SdThemeComponent, ThemesEnum, ThemesTextEnum, ThemesUtils
} from '@seb/web-design-system/core';

@Component({
  selector: 'sd-subcategories',
  templateUrl: './subcategories.component.html',
  styleUrls: ['./subcategories.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdListComponent,
    SdSubcategoryComponent,
    NgTemplateOutlet,
    SdPushCardComponent
  ]
})
export class SdSubcategoriesComponent extends SdThemeComponent implements OnInit, OnChanges {
  @Input()
  isLoading = false;

  @Input()
  largerSubcategories: boolean;

  @Input()
  linkCardSubcategories: boolean;

  /**
   * Allow to define the theme for the content based on the parent's theme config
   * * @param {string} dark | light | promo-1 | promo-2 | stars | extra | brand
   */
  @Input()
  parentsThemesConfig: ThemesEnum | BrandThemesEnum = ThemesEnum.light;

  @Input()
  pushTemplate: TemplateRef<SdPushCardComponent>;

  @Input()
  hasNoPushData = false;

  /**
   * Manage the number of subcat per row: default = 5, larger = 4
   */
  @HostBinding('class.is-large')
  @Input()
  isLarge: boolean;

  @Input()
  subcategories: ISubcategory[];

  @ViewChild('pushTemplate')
  pushTemplateEl: TemplateRef<any>;

  @Output()
  subCatClicked = new EventEmitter<ILink>();

  @Input() class = '';
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.isLarge ? 'is-large' : '',
      this.parentsThemesConfig ? 'has-parent-' + this.parentsThemesConfig : '',
      this.contentThemesConfig ? 'is-theme-' + this.contentThemesConfig : '',
      this.linkCardSubcategories ? 'has-link-card' : '',
      this.hasNoPushData ? 'has-no-push' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  positionEnum = PositionEnum;
  pushSizeEnum = PushSizeEnum;
  imageRatioEnum = ImageRatioEnum;
  themesEnum = ThemesEnum;
  contentThemesConfig: ThemesTextEnum;
  manageThemesTextConfigUtils = ThemesUtils.manageThemesTextConfig;

  constructor(private sdLibConfig: SdLibConfig) {
    super();
  }

  ngOnInit(): void {
    this.isLarge ??= this.largerSubcategories;
    this.manageThemesTextConfig(this.parentsThemesConfig);
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    this.isLarge ??= this.largerSubcategories;

    const { largerSubcategories, linkCardSubcategories } = this.sdLibConfig.get(this.brand);

    this.largerSubcategories = this.largerSubcategories ?? largerSubcategories;
    this.linkCardSubcategories = this.linkCardSubcategories ?? linkCardSubcategories;

    this.manageThemesTextConfig(this.parentsThemesConfig);
  }

  manageThemesTextConfig(theme: ThemesEnum | BrandThemesEnum): void {
    this.contentThemesConfig = this.manageThemesTextConfigUtils(theme);
  }

  onSubCatLinkClick(element: ILink): void {
    this.subCatClicked.emit(element);
  }
}
